import {
  AlertTriangle,
  CheckCircle,
  Code2,
  Database,
  Eye,
  EyeOff,
  Loader2,
  TerminalSquare,
  Users,
  XCircle,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Badge } from '@/components/admin/shared/badge'
import { AutoSaveInput } from '@/components/ui/auto-save-input'
import { AutoSaveMultiSelect } from '@/components/ui/auto-save-multi-select'
import { Input } from '@/components/ui/input'
import type { ProblemType } from '@/db/schema/enums'
import { AUTO_SAVE_DEBOUNCE_MS } from '@/hooks/use-auto-save'
import { assertNever } from '@/lib/assert-never'
import {
  PROBLEM_TAG_VOCABULARY,
  type ProblemTagSlug,
} from '@/lib/constants/problem-tags'
import { isOwningSourceId, type ProblemOwner } from '@/lib/problems/attribution'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import type { ProblemEditorData } from '../problem-editor'
import type { ProblemSource } from '../types'
import { resolveProblemOwner } from './source-candidates'
import { SourcesSection } from './sources-section'

// Status options
const STATUS_OPTIONS = [
  {
    value: 'draft' as const,
    label: 'Draft',
    icon: EyeOff,
    description: 'Only visible to admins',
  },
  {
    value: 'published' as const,
    label: 'Published',
    icon: CheckCircle,
    description: 'Visible based on visibility setting',
  },
]

// Problem.type options — the coding surface the harness renders.
const TYPE_OPTIONS = [
  {
    value: 'algorithms' as const,
    label: 'Algorithms',
    icon: Code2,
    description: 'Standard code editor',
  },
  {
    value: 'sql' as const,
    label: 'SQL',
    icon: Database,
    description: 'Schema browser, seed data, and a result grid',
  },
  {
    value: 'shell' as const,
    label: 'Shell',
    icon: TerminalSquare,
    description: 'Terminal environment',
  },
]

// Visibility options
const VISIBILITY_OPTIONS = [
  {
    value: 'public' as const,
    label: 'Public',
    icon: Eye,
    description: 'Visible to all users',
  },
  {
    value: 'client_only' as const,
    label: 'Clients Only',
    icon: Users,
    description: 'Admins and coaching clients',
  },
  {
    value: 'admin_only' as const,
    label: 'Admin Only',
    icon: EyeOff,
    description: 'Only visible to admins',
  },
]

interface Company {
  id: string
  name: string
  slug: string
  imgUrl?: string | null
  aliases?: string[]
}

interface MetadataSectionProps {
  formData: ProblemEditorData
  onUpdate: (updates: Partial<ProblemEditorData>) => void
}

// Validation result type
interface ValidationResult {
  valid: boolean
  errors: string[]
}

export function MetadataSection({ formData, onUpdate }: MetadataSectionProps) {
  const validateMutation = trpc.admin.questions.validate.useMutation()
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null)

  // Load companies via tRPC
  const { data: companiesData } = trpc.admin.companies.list.useQuery()
  const companies = companiesData?.data ?? []

  // Update mutation for saving fields
  const updateMutation = trpc.admin.questions.update.useMutation()

  // Ref to hold current formData for use in callbacks (avoids stale closures)
  const formDataRef = useRef(formData)
  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  // Save a field to the API
  const saveField = async (field: keyof ProblemEditorData, value: unknown) => {
    // Use ref to get current formData (avoids stale closure)
    const currentFormData = formDataRef.current
    if (!currentFormData.id) {
      throw new Error('Cannot save: Problem not yet created')
    }

    await updateMutation.mutateAsync({
      id: currentFormData.id,
      [field]: value,
    })

    // Local state is already updated optimistically by the caller
  }

  // Handle stats changes with auto-calculation of acceptance rate
  const handleStatsChange = async (
    field: 'totalAccepted' | 'totalSubmitted',
    value: string,
  ) => {
    const numValue = value === '' ? null : Number.parseInt(value, 10)

    // Use ref to get current formData (avoids stale closure)
    const currentFormData = formDataRef.current

    // Get the other value
    const accepted =
      field === 'totalAccepted' ? numValue : currentFormData.totalAccepted
    const submitted =
      field === 'totalSubmitted' ? numValue : currentFormData.totalSubmitted

    // Calculate acceptance rate if both values are present and submitted > 0
    let acceptanceRate: number | null = null
    if (
      accepted !== null &&
      accepted !== undefined &&
      submitted !== null &&
      submitted !== undefined &&
      submitted > 0
    ) {
      acceptanceRate = (accepted / submitted) * 100
    }

    // Update local state immediately
    onUpdate({
      [field]: numValue,
      acceptanceRate,
    })

    // Save both fields in a single API call to avoid race conditions
    if (!currentFormData.id) {
      throw new Error('Cannot save: Problem not yet created')
    }

    await updateMutation.mutateAsync({
      id: currentFormData.id,
      [field]: numValue,
      acceptanceRate,
    })

    // Local state is already updated optimistically above
  }

  // Handle Problem.type change (validates like status/visibility: revert on error)
  const handleTypeChange = async (newType: ProblemType) => {
    onUpdate({ type: newType })
    try {
      await saveField('type', newType)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
      onUpdate({ type: formData.type })
    }
  }

  const handleAddProblemTag = async (slug: string) => {
    // The multi-select's options are drawn from PROBLEM_TAG_VOCABULARY, so any
    // value it hands back is a known ProblemTagSlug.
    const tagSlug = slug as ProblemTagSlug
    if (formData.problemTagSlugs?.includes(tagSlug)) return

    const newSlugs = [...(formData.problemTagSlugs || []), tagSlug]
    onUpdate({ problemTagSlugs: newSlugs })
    try {
      await saveField('problemTagSlugs', newSlugs)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
      onUpdate({ problemTagSlugs: formData.problemTagSlugs })
    }
  }

  const handleRemoveProblemTag = async (slug: string) => {
    const newSlugs = (formData.problemTagSlugs || []).filter((s) => s !== slug)
    onUpdate({ problemTagSlugs: newSlugs })
    try {
      await saveField('problemTagSlugs', newSlugs)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
      onUpdate({ problemTagSlugs: formData.problemTagSlugs })
    }
  }

  const handleAddCompany = async (companySlug: string) => {
    if (!companySlug || formData.companies?.includes(companySlug)) return

    const newCompanies = [...(formData.companies || []), companySlug]
    onUpdate({ companies: newCompanies })
    try {
      await saveField('companies', newCompanies)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
      onUpdate({ companies: formData.companies })
    }
  }

  const handleRemoveCompany = async (companySlug: string) => {
    const newCompanies = (formData.companies || []).filter(
      (c) => c !== companySlug,
    )
    onUpdate({ companies: newCompanies })
    try {
      await saveField('companies', newCompanies)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
      onUpdate({ companies: formData.companies })
    }
  }

  const handleSourcesUpdate = (sources: ProblemSource[]) => {
    onUpdate({ sources })
    // Also update top-level fields from primary source for backward compatibility
    const primarySource = sources.find((source) => isOwningSourceId(source.id))
    if (primarySource) {
      onUpdate({
        name: primarySource.title || formData.name,
        externalId: primarySource.externalId,
        difficulty: primarySource.difficulty?.name ?? null,
        premiumRequired: primarySource.premiumRequired,
        url: primarySource.url,
        editorialUrl: primarySource.editorialUrl,
        source: primarySource.source,
        sourceIcon: primarySource.sourceIcon,
      })
    }
  }

  // Handle status change (validates when publishing)
  const handleStatusChange = async (
    newStatus: 'draft' | 'published',
  ): Promise<void> => {
    // Clear any previous validation errors when switching to draft
    if (newStatus === 'draft') {
      setValidationResult(null)
      onUpdate({ status: newStatus })
      try {
        await saveField('status', newStatus)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to save')
        onUpdate({ status: formData.status })
      }
      return
    }

    // Publishing - validate first
    if (!formData.id) return

    // Track if we default visibility (needed for error rollback)
    const defaultedVisibility = !formData.visibility

    setValidating(true)
    setValidationResult(null)

    try {
      const result: ValidationResult = await validateMutation.mutateAsync({
        problemId: formData.id!,
      })

      if (!result.valid) {
        // Validation failed - show errors, don't change status
        setValidationResult(result)
        toast.error(
          `Cannot publish: ${result.errors.length} issue${result.errors.length !== 1 ? 's' : ''} found`,
        )
        return
      }

      // Validation passed - proceed with publishing
      setValidationResult(null)

      // Default visibility to admin_only if not set
      const updates: Partial<ProblemEditorData> = { status: 'published' }
      if (defaultedVisibility) {
        updates.visibility = 'admin_only'
      }
      onUpdate(updates)

      // Save status (and visibility if defaulted)
      if (defaultedVisibility) {
        await saveField('visibility', 'admin_only')
      }
      await saveField('status', 'published')
      toast.success('Problem published successfully')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to publish')
      // Revert on failure - restore both status and visibility if we defaulted it
      const revertUpdates: Partial<ProblemEditorData> = {
        status: formData.status,
      }
      if (defaultedVisibility) {
        revertUpdates.visibility = formData.visibility
      }
      onUpdate(revertUpdates)
    } finally {
      setValidating(false)
    }
  }

  // A problem BCTCI does not own is imported content. Making it public shows
  // it to logged-out and free users, which is exactly what the client wall
  // exists to prevent (LG-409, M.2) — so this warns rather than blocks: an
  // admin may still want a specific imported problem public on purpose.
  //
  // Both the decision and the wording come from the shared rule rather than
  // being read off the row here: an owner we cannot resolve is its own case,
  // and it is the one that used to print "owned by Unknown Source" as if that
  // were a company.
  // Kept as the narrowed owner rather than collapsed into a boolean: the
  // warning's wording switches on the same value the gate tested, so a new
  // `ProblemOwner` arm — LG-880 adds one — is a compile error here instead of a
  // sentence quietly claiming we cannot identify an owner we just named.
  const owner = resolveProblemOwner(formData.sources ?? [])
  const importedOwner: Exclude<ProblemOwner, { kind: 'bctci' }> | null =
    formData.visibility === 'public' && owner.kind !== 'bctci' ? owner : null

  // Handle visibility change
  const handleVisibilityChange = async (
    newVisibility: 'public' | 'client_only' | 'admin_only',
  ): Promise<void> => {
    onUpdate({ visibility: newVisibility })
    try {
      await saveField('visibility', newVisibility)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
      // Revert on failure
      onUpdate({ visibility: formData.visibility })
    }
  }

  return (
    <div className="space-y-8">
      {/* Publishing Status Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Publishing Status</h3>
            <p className="text-muted-foreground text-sm">
              Control when and who can see this problem.
            </p>
          </div>
          {formData.status === 'draft' && (
            <Badge variant="paused" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              Draft - Not visible to users
            </Badge>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Status Selector */}
          <div>
            <label className="text-muted-foreground mb-2 block text-sm font-medium">
              Status
            </label>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map((option) => {
                const Icon = option.icon
                const isSelected = formData.status === option.value
                const isPublishing = validating && option.value === 'published'
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleStatusChange(option.value)}
                    disabled={!formData.id || validating}
                    className={cn(
                      'flex flex-1 items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                      isSelected
                        ? option.value === 'published'
                          ? 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-400'
                          : 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-muted',
                      (!formData.id || validating) &&
                        'cursor-not-allowed opacity-50',
                    )}
                  >
                    {isPublishing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                    <span>{isPublishing ? 'Validating...' : option.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Visibility Selector */}
          <div>
            <label className="text-muted-foreground mb-2 block text-sm font-medium">
              Visibility
              {formData.status === 'draft' && (
                <span className="text-muted-foreground/70 ml-1 font-normal">
                  (applies when published)
                </span>
              )}
            </label>
            <div className="flex gap-2">
              {VISIBILITY_OPTIONS.map((option) => {
                const Icon = option.icon
                const isSelected = formData.visibility === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleVisibilityChange(option.value)}
                    disabled={!formData.id}
                    title={option.description}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-2 text-xs transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-muted',
                      !formData.id && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    <span className="hidden sm:inline">{option.label}</span>
                  </button>
                )
              })}
            </div>
            {importedOwner && (
              // Announced, because it appears in direct response to clicking
              // Public: without a role a screen-reader admin hears the button's
              // own state change and never learns the guard just fired.
              <div
                role="alert"
                className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
              >
                <AlertTriangle
                  className="mt-0.5 h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span>
                  {importedOwner.kind === 'outside' ? (
                    <>
                      This problem is owned by{' '}
                      <strong>{importedOwner.name}</strong>, not BCTCI.{' '}
                    </>
                  ) : importedOwner.kind === 'unknown' ? (
                    <>
                      This problem has no owning source we can identify, so we
                      cannot show that BCTCI owns it.{' '}
                    </>
                  ) : (
                    assertNever(importedOwner, 'Unexpected problem owner')
                  )}
                  Public visibility shows imported content to logged-out and
                  free users. Prefer <em>Clients</em> unless this is deliberate.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Validation Errors (shown when publishing fails) */}
        {validationResult && !validationResult.valid && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-medium">
                <XCircle className="h-4 w-4" />
                <span>
                  Cannot publish: {validationResult.errors.length} issue
                  {validationResult.errors.length !== 1 ? 's' : ''} found
                </span>
              </div>
              <ul className="list-inside list-disc space-y-1 pl-1 text-xs">
                {validationResult.errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-border border-t" />

      {/* Sources Section - Primary and editable */}
      <SourcesSection
        problemId={formData.id}
        formData={formData}
        sources={formData.sources || []}
        onSourcesUpdate={handleSourcesUpdate}
        onFormUpdate={onUpdate}
      />

      {/* Divider */}
      <div className="border-border border-t" />

      {/* Problem-Specific Details */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Problem-Specific Details</h3>
        <p className="text-muted-foreground -mt-4 text-sm">
          These fields are shared across all sources for this problem.
        </p>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Accept %
            </label>
            <Input
              type="text"
              value={
                formData.acceptanceRate
                  ? `${formData.acceptanceRate.toFixed(2)}%`
                  : '—'
              }
              readOnly
              disabled
              className="bg-muted cursor-not-allowed"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Freq Bar
            </label>
            <AutoSaveInput
              type="text"
              value={formData.freqBar?.toString() ?? ''}
              onChange={(val) => {
                // Keep as string during typing, don't parse yet
                const parsed = val === '' ? null : Number.parseFloat(val)
                onUpdate({
                  freqBar:
                    parsed === null || Number.isNaN(parsed) ? null : parsed,
                })
              }}
              onSave={async (val) => {
                // Validate input before saving
                if (
                  val !== '' &&
                  (Number.isNaN(Number.parseFloat(val)) ||
                    !/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(val))
                ) {
                  const error = new Error('Please enter a valid number')
                  toast.error(error.message)
                  throw error
                }
                const numVal = val === '' ? null : Number.parseFloat(val)
                try {
                  await saveField('freqBar', numVal)
                } catch (error) {
                  toast.error(
                    error instanceof Error ? error.message : 'Failed to save',
                  )
                  throw error
                }
              }}
              debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
              placeholder="93.79"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Accepted
            </label>
            <AutoSaveInput
              type="text"
              value={formData.totalAccepted?.toString() ?? ''}
              onChange={(val) => {
                // Keep as string during typing to avoid parsing issues
                // Only update if it's a valid number or empty
                if (val === '' || /^\d+$/.test(val)) {
                  onUpdate({
                    totalAccepted: val === '' ? null : Number.parseInt(val, 10),
                  })
                }
              }}
              onSave={async (val) => {
                // Validate input before saving
                if (
                  val !== '' &&
                  (Number.isNaN(Number.parseInt(val, 10)) || !/^\d+$/.test(val))
                ) {
                  const error = new Error('Please enter a valid number')
                  toast.error(error.message)
                  throw error
                }
                try {
                  await handleStatsChange('totalAccepted', val)
                } catch (error) {
                  toast.error(
                    error instanceof Error ? error.message : 'Failed to save',
                  )
                  throw error
                }
              }}
              debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
              placeholder="1699077"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Submitted
            </label>
            <AutoSaveInput
              type="text"
              value={formData.totalSubmitted?.toString() ?? ''}
              onChange={(val) => {
                // Keep as string during typing to avoid parsing issues
                // Only update if it's a valid number or empty
                if (val === '' || /^\d+$/.test(val)) {
                  onUpdate({
                    totalSubmitted:
                      val === '' ? null : Number.parseInt(val, 10),
                  })
                }
              }}
              onSave={async (val) => {
                // Validate input before saving
                if (
                  val !== '' &&
                  (Number.isNaN(Number.parseInt(val, 10)) || !/^\d+$/.test(val))
                ) {
                  const error = new Error('Please enter a valid number')
                  toast.error(error.message)
                  throw error
                }
                try {
                  await handleStatsChange('totalSubmitted', val)
                } catch (error) {
                  toast.error(
                    error instanceof Error ? error.message : 'Failed to save',
                  )
                  throw error
                }
              }}
              debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
              placeholder="1901107"
            />
          </div>
        </div>

        {/* Type Selector */}
        <div>
          <label className="text-muted-foreground mb-2 block text-sm font-medium">
            Type
          </label>
          <div className="flex gap-2">
            {TYPE_OPTIONS.map((option) => {
              const Icon = option.icon
              const isSelected = formData.type === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTypeChange(option.value)}
                  disabled={!formData.id}
                  title={option.description}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted',
                    !formData.id && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{option.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Problem Tags and Companies in a grid */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Problem Tags */}
          <AutoSaveMultiSelect<(typeof PROBLEM_TAG_VOCABULARY)[number]>
            label="Problem Tags"
            value={formData.problemTagSlugs || []}
            onAdd={handleAddProblemTag}
            onRemove={handleRemoveProblemTag}
            options={[...PROBLEM_TAG_VOCABULARY]}
            getOptionValue={(t) => t.slug}
            getOptionLabel={(t) => t.name}
            allowFreeForm={false}
            inputPlaceholder="Search tags..."
            selectPlaceholder="Search tags..."
            badgeVariant="topic"
            disabled={!formData.id}
          />

          {/* Companies */}
          <AutoSaveMultiSelect<Company>
            label="Companies"
            labelInfo={`${companies.length} total`}
            value={formData.companies || []}
            onAdd={handleAddCompany}
            onRemove={handleRemoveCompany}
            options={companies}
            getOptionValue={(c) => c.slug}
            getOptionLabel={(c) => c.name}
            getOptionSearchText={(c) =>
              `${c.name} ${c.slug} ${(c.aliases || []).join(' ')}`
            }
            allowFreeForm={false}
            selectPlaceholder="Search companies..."
            badgeVariant="topic"
            disabled={!formData.id}
          />
        </div>
      </div>
    </div>
  )
}
